import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  withStyles,
  Button,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  CircularProgress,
  Grid,
  Typography,
  FormControl,
  FormControlLabel,
  FormHelperText,
  RadioGroup,
  Radio,
  Icon,
  InputAdornment,
  TextField,
} from '@material-ui/core';

// actions
import { showLogsModal } from '@pingum/app-state-management/log/actions';
import { updateTenant } from '@pingum/app-state-management/tenant/actions';

// helpers
import { isCardValid } from '../PaymentMethodCard';

// pingum components
import FeatureHighlights from '../../../../widgets/FeatureHighlights';
import AuditLogsButton from '../../../../widgets/AuditLogsButton';
import DisplayMessage from '../../../../widgets/DisplayMessage';
import BillingAndPricingFaq from './components/BillingAndPricingFaq';

import {
  getBillingInformation,
  getBillingAmounts,
  getPeriodForPerUserPlan,
} from '../../../../../utils/pricingHelper';
import periods from '../../../../../utils/periods';
import { daysRemaining } from '../../../../../utils/dateHelper';

import MissingPaymentMethodModal from './components/MissingPaymentMethodModal';
import ConfirmDeactivateOrganizationModal from './components/ConfirmDeactivateOrganizationModal';
import ConfirmSubscribeToPlanModal from './components/ConfirmSubscribeToPlanModal';

import styles from './styles';

export class SubscriptionCardComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      openMissingPaymentMethodModal: false,
      openConfirmSubscribeToPlanModal: false,
      openDeactivateOrganizationModal: false,
      selectedPeriod: null,
      userLimit: 0,
      error: null,
      loadingAction: 'subscribe',
    };
  }

  componentDidMount() {
    if (this.props.tenant && this.props.tenant.stripePlan) {
      this.setState({
        selectedPeriod: getPeriodForPerUserPlan(this.props.tenant.stripePlan),
        userLimit: this.props.tenant.userLimit || 0,
      });
    }
  }

  handleChangePeriod = (e) => {
    this.setState({ selectedPeriod: periods[e.target.value] });
  }

  handleChangeUserLimit = (e) => {
    const newUserLimit = e.target.value < 1
      ? 1
      : Number.parseInt(e.target.value, 10);
    this.setState({
      userLimit: newUserLimit > 200
        ? 200
        : newUserLimit,
    });
  }

  showLogsModal = () => {
    this.props.showLogsModal({
      tenantName: this.props.tenant.name,
      service: 'tenant',
      actions: ['Subscribed to Plan', 'Updated Subscription'],
    });
  }

  handleSubscribeToPlan = () => {
    const {
      selectedPeriod,
      userLimit,
    } = this.state;
    if ((selectedPeriod === null && userLimit == null)
      || (selectedPeriod !== null && selectedPeriod.perUserPlan === this.props.tenant.stripePlan && userLimit === this.props.tenant.userLimit)) {
      // no changes
      return;
    }

    if (this.props.tenant.stripeCardId === null
        || !isCardValid(
          this.props.stripeCard.address_zip_check,
          this.props.stripeCard.cvc_check,
          this.props.stripeCard.exp_year,
          this.props.stripeCard.exp_month,
        )) {
      // must have a valid card on file
      this.setState({ openMissingPaymentMethodModal: true });
    } else {
      this.setState({
        openConfirmSubscribeToPlanModal: true,
        loadingAction: 'subscribe',
      });
    }
  }

  handleCloseMissingPaymentMethodModal = () => {
    this.setState({ openMissingPaymentMethodModal: false });
  }

  handleCloseConfirmSubscribeToPlanModal = () => {
    this.setState({ openConfirmSubscribeToPlanModal: false });
    this.props.updateTenant({
      name: this.props.tenant.name,
      displayName: this.props.tenant.displayName,
      // only allow free trial if tenant hasn't had one already
      userLimit: this.state.userLimit,
      stripePlan: this.state.selectedPeriod.perUserPlan,
    }).then(() => {
      this.setState({ error: null });
    }).catch((error) => {
      this.setState({ error: error.message || null });
    });
  }

  handleCloseSubscribeToPlanModal = () => {
    this.setState({ openConfirmSubscribeToPlanModal: false });
  }

  handleOpenDeactivateOrganization = () => {
    this.setState({
      openDeactivateOrganizationModal: true,
      loadingAction: 'deactivate',
    });
  }

  handleCloseDeactivateOrganization = () => {
    this.setState({ openDeactivateOrganizationModal: false });
  }

  handleConfirmDeactivateOrganizationModal = () => {
    this.setState({ openDeactivateOrganizationModal: false });
    this.props.updateTenant({
      name: this.props.tenant.name,
      displayName: this.props.tenant.displayName,
      deactivated: true,
    });
  }

  canChangeSubscription = () => {
    const {
      userLimit,
      selectedPeriod,
    } = this.state;
    const {
      userLimit: currentUserLimit,
      stripePlan: currentStripePlan,
    } = this.props.tenant;
    return !!userLimit && !!selectedPeriod && userLimit < 200
    && !(userLimit === currentUserLimit && selectedPeriod.perUserPlan === currentStripePlan);
  }

  render() {
    const {
      selectedPeriod,
      userLimit,
      error,
      loadingAction,
    } = this.state;
    const {
      classes,
      tenant,
      isFetching,
    } = this.props;
    const billingInformation = getBillingInformation(tenant);

    return (
      <Grid container spacing={3} justify="center">

        <Grid item xs={12} md={6}>
          <Card className={classes.subscriptionCard}>
            <CardHeader
              title="Subscription"
              subheader="Organization current Pingum subcription"
              action={
                <AuditLogsButton onClick={this.showLogsModal} />
            }
            />
            <CardContent>
              <div className={classes.subscriptionMessageContainer}>
                <DisplayMessage
                  icon={billingInformation.billingIcon}
                  iconColor={billingInformation.billingColor}
                  title={billingInformation.billingHeadline}
                  subTitle={billingInformation.billingDescription}
                />
              </div>
            </CardContent>
            {
              (billingInformation.isOnFreeTrial && billingInformation.hasValidPaidPlan) && (
                <div>
                  <Divider />
                  <CardActions className={classes.cardButtonContainer}>
                    <Typography align="center" color="primary" variant="subtitle1">
                      Free Trial Still Active - First Invoice In {daysRemaining(tenant.freeTrialEndDate)} Days
                    </Typography>
                  </CardActions>
                </div>
              )
            }
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card className={classes.subscriptionCard}>
            <CardHeader
              title={!billingInformation.hasValidPaidPlan
                ? 'Start Your Subscription'
                : 'Change Your Subscription'}
              subheader={<div>View full pricing and feature details on the <a href="https://pingum.io/pricing" target="blank">pricing page</a>.</div>}
            />
            <CardContent>

              <FormControl component="fieldset" variant="outlined" className={classes.periodFormControl} fullWidth>
                <h4 className={classes.billingPeriodSavings}>
                  Pay:
                </h4>
                <RadioGroup
                  classes={{ root: classes.periodGroup }}
                  row
                  aria-label="Billing Period"
                  name="billing-period-length"
                  value={selectedPeriod
                    ? selectedPeriod.label
                    : null}
                  onChange={this.handleChangePeriod}
                >
                  <FormControlLabel classes={{ label: classes.periodRadio }} value={periods.month.label} control={<Radio classes={{ root: classes.periodRadio }} />} label="Monthly" />
                  <FormControlLabel classes={{ label: classes.periodRadio }} value={periods.year.label} control={<Radio classes={{ root: classes.periodRadio }} />} label="Annually" />
                </RadioGroup>
                <FormHelperText component="div">
                  <h4 className={classes.billingPeriodSavings}>
                    You save {periods.year.discount * 100}% when paying annually
                  </h4>
                </FormHelperText>
              </FormControl>

              <hr />
              <div>
                <Typography variant="subtitle1" align="center" gutterBottom>
                  Paid Organization Users
                </Typography>
                <div className={classes.paidUsersInputConatiner}>
                  <TextField
                    id="org-users"
                    fullWidth
                    type="number"
                    value={userLimit || tenant.userLimit || 0}
                    onChange={this.handleChangeUserLimit}
                    className={classes.paidUsersInput}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon>groups</Icon>
                        </InputAdornment>
                      ),
                      inputProps: {
                        min: 1,
                        max: 200,
                      },
                    }}
                  />
                </div>

              </div>
              {
                // eslint-disable-next-line no-nested-ternary
                (!!userLimit && !!selectedPeriod)
                  ? (
                    (userLimit && userLimit >= 200)
                      ? (
                        <Typography
                          variant="caption"
                          component="div"
                          color="secondary"
                          align="center"
                        >Contact Us About Our Enterprise Plan!
                        </Typography>
                      )
                      : getBillingAmounts(userLimit, selectedPeriod)
                  )
                  : (<Typography variant="caption" component="div" align="center" gutterBottom>Select payment frequency and set the number of paid users</Typography>)
              }
            </CardContent>

            <Divider />
            <CardActions className={classes.cardButtonContainer}>
              {
                error && <Typography variant="caption" component="div" align="center" color="error" gutterBottom>{error || 'There was an error, please contact support'}</Typography>
              }
              <Button size="small" onClick={this.handleOpenDeactivateOrganization} disabled={isFetching}>
                {
                  (isFetching && loadingAction === 'deactivate')
                  && <CircularProgress size={20} className={this.props.classes.progressButton} />
                }
                Deactivate Organization
              </Button>
              <Button color="primary" variant="contained" size="small" onClick={this.handleSubscribeToPlan} disabled={isFetching || !this.canChangeSubscription()}>
                {
                  (isFetching && loadingAction === 'subscribe')
                  && <CircularProgress id="subscribe-and-purchase-plan" size={20} className={this.props.classes.progressButton} />
                }
                { !billingInformation.hasValidPaidPlan
                  ? 'Start Subscription'
                  : 'Update Subscription'}
              </Button>
              {
                /*
                  LinkedIn Insight Pixel
                  - Fires conversion for new purchase/customer
                  - do not remove unless you know what you are doing
                  - ONLY SHOW IN PRODUCTION
                */
                ((isFetching && loadingAction === 'subscribe' && !billingInformation.hasValidPaidPlan) && window.location.origin === 'https://support.pingum.io') && (
                  <img height="1" width="1" style={{ display: 'none' }} alt="" src="https://px.ads.linkedin.com/collect/?pid=2693132&conversionId=3946252&fmt=gif" />
                )
              }
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Typography align="center" variant="h3" gutterBottom>
            Feature Highlights
          </Typography>
          <FeatureHighlights />
        </Grid>

        <Grid item xs={12} lg={10} xl={8}>
          <Typography align="center" variant="h3" gutterBottom>
            Billing FAQs
          </Typography>
          <BillingAndPricingFaq includeOrgFaq />
        </Grid>
        <MissingPaymentMethodModal
          isOpen={this.state.openMissingPaymentMethodModal}
          handleClose={this.handleCloseMissingPaymentMethodModal}
        />
        <ConfirmSubscribeToPlanModal
          isOpen={this.state.openConfirmSubscribeToPlanModal}
          handleClose={this.handleCloseSubscribeToPlanModal}
          handleCloseConfirm={this.handleCloseConfirmSubscribeToPlanModal}
          currentBillingInformation={billingInformation}
          newUserLimit={userLimit}
          newPeriod={selectedPeriod}
        />
        <ConfirmDeactivateOrganizationModal
          isOpen={this.state.openDeactivateOrganizationModal}
          handleClose={this.handleCloseDeactivateOrganization}
          handleCloseConfirm={this.handleConfirmDeactivateOrganizationModal}
        />
      </Grid>
    );
  }
}

SubscriptionCardComponent.propTypes = {
  classes: PropTypes.object,
  tenant: PropTypes.object,
  updateTenant: PropTypes.func,
  showLogsModal: PropTypes.func,
  isFetching: PropTypes.bool,
  stripeCard: PropTypes.object,
};

SubscriptionCardComponent.defaultProps = {
  classes: {},
  tenant: {
    name: '',
    displayName: '',
    stripePlan: '',
    stripeCardId: null,
  },
  updateTenant: () => {},
  showLogsModal: () => {},
  isFetching: true,
  stripeCard: {},
};

export const mapStateToProps = (state) => ({ isFetching: state.tenant.isFetchingUpdateTenant });

export const mapDispatchToProps = (dispatch) => ({
  updateTenant: (request) => dispatch(updateTenant(request)),
  showLogsModal: (request) => dispatch(showLogsModal(request)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(SubscriptionCardComponent));
